import React, { useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Grid,
  Stack,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Paper,
} from "@mui/material";

import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BusinessIcon from "@mui/icons-material/Business";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import PeopleIcon from "@mui/icons-material/People";
import ListAltIcon from "@mui/icons-material/ListAlt";
import AssessmentIcon from "@mui/icons-material/Assessment";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import TimelineIcon from "@mui/icons-material/Timeline";
import SecurityIcon from "@mui/icons-material/Security";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";

import InsightsIcon from "@mui/icons-material/Insights";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import FeedIcon from "@mui/icons-material/Feed";

import { Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";


const HERO_ILLU = process.env.PUBLIC_URL + "/images/workpulse-what-you-can-do.png";



// SEO constants (safe defaults)
const SEO = {
  canonical: "https://workpulse.in/",
  title: "WorkPulse | Entity-wise Work & Compliance Tracking for CA Firms",
  description:
    "WorkPulse helps Chartered Accountants manage entity-wise projects, tasks, due dates and review workflows. Track team progress, maintain an audit trail, and request a demo for your CA firm.",
  ogImage: "https://workpulse.in/og/workpulse-home.png", // add later in /public/og/ (won’t break if missing)
};

const FAQS = [
  {
    q: "What is WorkPulse.in and who is it for?",
    a: "WorkPulse.in is a single workspace to plan projects, assign tasks, log time, and view insights—ideal for firms managing multiple clients/entities and teams.",
  },
  {
    q: "How secured is my information with WorkPulse.in?",
    a: "At WorkPulse.in your data is encrypted in transit (TLS) and at rest, and access is restricted by role-based rules with company-level scoping. Google authentication services maintains industry-standard security and compliance for the underlying infrastructure; WorkPulse.in allows role based access so only authorized users in your organization can view your information. We don’t sell your data. We only process anonymized, aggregate usage metrics (e.g., total users, total tasks, total storage used etc.) to gauge features usage and manage capacity. WorkPulse.in never reads the text of your entities, projects, tasks, users or notes.",
  },
  {
    q: "How do I get started?",
    a: "Create an Entity, add Projects under it, invite users, assign managers/team leaders, then start adding Tasks. Your team can immediately begin logging time and tracking progress.",
  },
  {
    q: "How does role-based access work?",
    a: "Admins see everything. Managers see projects they manage (and all tasks inside). Members see their own tasks and any project where they are Team Leader, including all tasks in that project.",
  },
  {
    q: "How do Projects & Stages help my team?",
    a: "Stages and priorities give everyone a shared picture of what’s in plan, in progress, or blocked—so deadlines are clear and bottlenecks surface early.",
  },
  {
    q: "What can I do with Tasks?",
    a: "Assign owners, set due dates and priority, add notes, and filter by entity, project, status, or dates. This keeps daily execution crisp and searchable.",
  },
  {
    q: "How do Timesheets work?",
    a: "Log hours in clean HH:MM per project/area. Weekly totals help teams review effort and reduce spreadsheet chaos.",
  },
  {
    q: "What does Timesheet Insights show?",
    a: "Monthly trends by user, project, entity, or area. Spot over/under-utilization, compare effort, and drill into details quickly.",
  },
  {
    q: "I’m a Member and also a Team Leader—what do I see?",
    a: "You’ll see your assigned tasks plus all tasks under any project you lead. That way you can manage your project end-to-end without extra permissions.",
  },
  {
    q: "Can I track who did what and when?",
    a: "Yes—use Activity Log and Login Snapshot to review changes and sign-ins for better accountability and audit trail.",
  },
  {
    q: "Does WorkPulse support multiple entities/clients?",
    a: "Absolutely. Organize projects by entity, keep teams separated, and rely on role-based visibility so the right people see the right work.",
  },
];

function upsertMeta({ selector, attrName, attrValue, content }) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attrName, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLinkCanonical(href) {
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

function upsertJsonLd(id, obj) {
  let script = document.getElementById(id);
  if (!script) {
    script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = id;
    document.head.appendChild(script);
  }
  script.text = JSON.stringify(obj);
}

export default function LandingPage() {

const navigate = useNavigate();

  // ✅ SEO injection without Helmet (prevents the errors you faced)
  useEffect(() => {
    document.title = SEO.title;

    // Basic
    upsertMeta({
      selector: 'meta[name="description"]',
      attrName: "name",
      attrValue: "description",
      content: SEO.description,
    });

    // Canonical
    upsertLinkCanonical(SEO.canonical);

    // OpenGraph
    upsertMeta({
      selector: 'meta[property="og:title"]',
      attrName: "property",
      attrValue: "og:title",
      content: "WorkPulse | Work Management for CA Firms",
    });
    upsertMeta({
      selector: 'meta[property="og:description"]',
      attrName: "property",
      attrValue: "og:description",
      content:
        "Entity-wise projects, task ownership, review cycles, dashboards and audit trail—built for Chartered Accountants in India.",
    });
    upsertMeta({
      selector: 'meta[property="og:url"]',
      attrName: "property",
      attrValue: "og:url",
      content: SEO.canonical,
    });
    upsertMeta({
      selector: 'meta[property="og:type"]',
      attrName: "property",
      attrValue: "og:type",
      content: "website",
    });
    upsertMeta({
      selector: 'meta[property="og:image"]',
      attrName: "property",
      attrValue: "og:image",
      content: SEO.ogImage,
    });

    // Twitter
    upsertMeta({
      selector: 'meta[name="twitter:card"]',
      attrName: "name",
      attrValue: "twitter:card",
      content: "summary_large_image",
    });
    upsertMeta({
      selector: 'meta[name="twitter:title"]',
      attrName: "name",
      attrValue: "twitter:title",
      content: "WorkPulse | Work Management for CA Firms",
    });
    upsertMeta({
      selector: 'meta[name="twitter:description"]',
      attrName: "name",
      attrValue: "twitter:description",
      content:
        "Entity-wise projects, task review, dashboards and audit trail—built for Chartered Accountants.",
    });
    upsertMeta({
      selector: 'meta[name="twitter:image"]',
      attrName: "name",
      attrValue: "twitter:image",
      content: SEO.ogImage,
    });

    // JSON-LD: Organization
    upsertJsonLd("wp-org-schema", {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "WorkPulse",
      url: "https://workpulse.in",
      logo: "https://workpulse.in/logo192.png",
      areaServed: "IN",
    });

    // JSON-LD: SoftwareApplication
    upsertJsonLd("wp-app-schema", {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "WorkPulse",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      url: "https://workpulse.in",
      description:
        "Entity-wise work management platform for CA firms to track compliance tasks, review workflows, dashboards and audit trail.",
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "INR",
        description: "Request a demo for pricing and onboarding.",
      },
    });

    // JSON-LD: FAQPage (matches your visible FAQ)
    upsertJsonLd("wp-faq-schema", {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
  }, []);

  return (
    <Box sx={{ bgcolor: "#fff" }}>

<Helmet>
  <title>WorkPulse.in | Entity-wise Project & Task Management for CA Firms</title>

  <meta
    name="description"
    content="WorkPulse helps Chartered Accountants manage entity-wise projects, tasks, timesheets, audit trail and dashboards in one secure workspace. Book a demo."
  />

  <link rel="canonical" href="https://workpulse.in/" />

  {/* OpenGraph */}
  <meta property="og:type" content="website" />
  <meta property="og:site_name" content="WorkPulse.in" />
  <meta property="og:url" content="https://workpulse.in/" />
  <meta property="og:title" content="Never lose track of client work" />
  <meta
    property="og:description"
    content="Entity-wise workflow, project management, timesheets and activity trail in one secure workspace. Built for Chartered Accountants in India."
  />
  <meta property="og:image" content="https://workpulse.in/og-workpulse.png" />
  <meta property="og:image:width" content="1200" />
  <meta property="og:image:height" content="630" />

  {/* Twitter (works even without a Twitter account) */}
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content="Never lose track of client work" />
  <meta
    name="twitter:description"
    content="Entity-wise workflow, project management, timesheets and activity trail in one secure workspace."
  />
  <meta name="twitter:image" content="https://workpulse.in/og-workpulse.png" />
</Helmet>



      {/* ===== HERO ===== */}
      <Box
        sx={{
          py: { xs: 10, md: 6 },
          background:
            "radial-gradient(1200px 400px at 10% 0%, rgba(25,118,210,.10), transparent 60%), radial-gradient(1200px 400px at 90% 0%, rgba(99,102,241,.10), transparent 60%)",
          borderBottom: "1px solid #eef2f7",
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={7}>
              <Stack spacing={2.5}>
                <Chip
                  label="Projects · Tasks · Timesheets · Insights"
                  color="primary"
                  variant="outlined"
                  sx={{ width: "fit-content" }}
                />

                {/* ✅ Semantic H1 without changing visual design */}
                <Typography component="h1" variant="h3" fontWeight={800} lineHeight={1.1}>
                  Plan, track and deliver work—<br />from a single, live workspace
                </Typography>

                <Typography variant="h6" color="text.secondary">
                  WorkPulse brings entities, projects, users and tasks into one place.
                  Organize projects by entity, assign owners and team leaders, track progress
                  by tasks, and keep effort data synced with timesheets and insights.
                  Clear permissions ensure admins, managers and members see exactly what they need.
                </Typography>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ pt: 1 }}>
                  {/* NEW */}
                  <Button component={RouterLink} to="/project-management" size="large" variant="outlined">
                    WorkPulse.in Overview
                  </Button>

                  <Button component={RouterLink} to="/signup" size="large" variant="outlined">
                    Get Started
                  </Button>

                  <Button component={RouterLink} to="/login" size="large" variant="contained">
                    Login
                  </Button>

                  <Button component={RouterLink} to="/contact" size="large" variant="outlined">
                    Contact Us
                  </Button>

                  
                </Stack>

                <Stack direction="row" spacing={1.25} sx={{ pt: 2 }} flexWrap="wrap">
                  <Chip icon={<PeopleAltIcon />} label="Role-based visibility" />
                  <Chip icon={<AccessTimeIcon />} label="Timesheet-ready" />
                  <Chip icon={<InsightsIcon />} label="Actionable insights" />
                </Stack>
              </Stack>
            </Grid>

            <Grid item xs={12} md={5}>
              <Card
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: 3,
                  border: "1px solid #e7ecf5",
                  boxShadow: "0 8px 24px rgba(16,24,40,.06)",
                }}
              >
                <CardMedia
                  component="img"
                  image={HERO_ILLU}
                  alt="WorkPulse dashboard overview for CA firms"
                  sx={{ borderRadius: 2 }}
                />
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ===== PROFESSIONALS SECTION ===== */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Typography variant="overline" color="text.secondary">
          Philosophy
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 800, mb: 2 }}>
          For the Professionals, By the Professionals, To the Professionals
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          At <b>WorkPulse.in</b>, we believe true innovation in work management comes only when
          professionals build solutions for professionals. Every feature we design, every dashboard
          we craft, and every update we release is shaped with one clear vision — to empower modern
          professionals to achieve more with clarity, speed, and confidence.
        </Typography>

        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #e7ecf5" }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                For the Professionals
              </Typography>
              <Typography color="text.secondary">
                Our platform is tailored to meet the real challenges of today’s workforce —
                managing projects, tracking tasks, coordinating teams, and streamlining timesheets.
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #e7ecf5" }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                By the Professionals
              </Typography>
              <Typography color="text.secondary">
                WorkPulse is crafted by experienced professionals who understand the complexities
                of managing entities, projects, and teams across industries.
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #e7ecf5" }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                To the Professionals
              </Typography>
              <Typography color="text.secondary">
                Our promise is to deliver powerful, intuitive, and reliable solutions back to the
                professional community, making work life simpler, smarter, and more impactful.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* ===== WHAT YOU CAN DO ===== */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Typography variant="overline" color="text.secondary">
          Overview
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 800, mb: 2 }}>
          What you can do in WorkPulse
        </Typography>
        <Grid container spacing={3}>
          {[
            "Create entities and projects with managers & team leads",
            "Assign users and track status across stages",
            "Capture hours by project & area in HH:MM",
            "Drill into monthly insights by user, project, and entity",
            "Filter tasks by status, priority, owner, or due dates",
            "Audit logins and actions with Activity Log",
          ].map((line) => (
            <Grid key={line} item xs={12} md={6}>
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: 1,
                    bgcolor: "#f4f7ff",
                    color: "primary.main",
                    display: "grid",
                    placeItems: "center",
                    mt: "2px",
                  }}
                >
                  <CheckCircleOutlineIcon fontSize="small" />
                </Box>
                <Typography>{line}</Typography>
              </Stack>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* ===== QUICK BENEFITS (3-up) ===== */}
      <Container maxWidth="lg" sx={{ py: { xs: 2, md: 2 } }}>
        <Grid container spacing={3}>
          {[
            {
              icon: <QueryStatsIcon />,
              title: "Clarity at a glance",
              desc:
                "Dashboards and summary pages reveal project load, task progress and pending work instantly.",
            },
            {
              icon: <TimelineIcon />,
              title: "Stay on schedule",
              desc:
                "Plan due dates, track completion, and spot bottlenecks before they turn into delays.",
            },
            {
              icon: <SecurityIcon />,
              title: "Right access for everyone",
              desc:
                "Admins, Managers, and Members see exactly what they should—nothing more, nothing less.",
            },
          ].map((f) => (
            <Grid key={f.title} item xs={12} md={4}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  border: "1px solid #e7ecf5",
                  height: "100%",
                }}
              >
                <Stack spacing={1.25}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2,
                      bgcolor: "#f4f7ff",
                      color: "primary.main",
                      display: "grid",
                      placeItems: "center",
                    }}
                  >
                    {f.icon}
                  </Box>
                  <Typography variant="h6">{f.title}</Typography>
                  <Typography color="text.secondary">{f.desc}</Typography>
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* ===== PRODUCT PILLARS ===== */}
      <Container maxWidth="lg" sx={{ pb: { xs: 6, md: 8 } }}>
        <Typography variant="overline" color="text.secondary">
          Product
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 800, mb: 3 }}>
          Everything your team needs to move work forward
        </Typography>

        <Grid container spacing={3}>
          {[
            {
              to: "/projects",
              icon: <WorkOutlineIcon />,
              title: "Projects & Stages",
              desc:
                "Create projects by entity, set status and priority, assign managers and team leads.",
              chip: "Plan",
            },
            {
              to: "/tasks",
              icon: <ListAltIcon />,
              title: "Tasks & Ownership",
              desc:
                "Assign tasks with due dates, priority and owners. Filter by status, entity, project and more.",
              chip: "Execute",
            },
            {
              to: "/timesheet",
              icon: <AssessmentIcon />,
              title: "Timesheets",
              desc:
                "Capture weekly hours by project and area with clean HH:MM entry and quick totals.",
              chip: "Track",
            },
            {
              to: "/timesheet-insights",
              icon: <QueryStatsIcon />,
              title: "Timesheet Insights",
              desc:
                "See monthly trends by user, project, entity or area. Compare totals and drill down fast.",
              chip: "Analyze",
            },
          ].map((card) => (
            <Grid key={card.title} item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  border: "1px solid #e7ecf5",
                  height: "100%",
                }}
              >
                <Stack spacing={1.25}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2,
                      bgcolor: "#f4f7ff",
                      color: "primary.main",
                      display: "grid",
                      placeItems: "center",
                    }}
                  >
                    {card.icon}
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="h6">{card.title}</Typography>
                    <Chip label={card.chip} size="small" />
                  </Stack>
                  <Typography color="text.secondary">{card.desc}</Typography>
                  <Box>
                    <Button component={RouterLink} to={card.to} variant="text">
                      Open
                    </Button>
                  </Box>
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* ===== ROLE-BASED ACCESS CALLOUT ===== */}
      <Container maxWidth="lg" sx={{ pb: { xs: 6, md: 8 } }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: 3,
            border: "1px solid #e7ecf5",
            background:
              "linear-gradient(180deg, rgba(99,102,241,.06), rgba(99,102,241,.02))",
          }}
        >
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
                Role-based access that mirrors your team
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                Admins see everything, Managers see projects they manage and all
                tasks under them, and Members see their own assigned projects
                plus any project they’re team leader for—including all tasks under
                those projects.
              </Typography>
              <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                <Chip icon={<PeopleIcon />} label="Admin" />
                <Chip icon={<PeopleIcon />} label="Manager" color="primary" variant="outlined" />
                <Chip icon={<PeopleIcon />} label="Member" variant="outlined" />
              </Stack>
            </Grid>
            <Grid item xs={12} md={5}>
              <Card elevation={0} sx={{ p: 2, borderRadius: 3, border: "1px solid #e7ecf5" }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Quick links
                  </Typography>
                  <Stack spacing={1}>
                    <Button component={RouterLink} to="/user-management" variant="outlined" size="small">
                      User Management
                    </Button>
                    <Button component={RouterLink} to="/project-information" variant="outlined" size="small">
                      Project Information
                    </Button>
                    <Button component={RouterLink} to="/user-task-summary" variant="outlined" size="small">
                      User & Task Summary
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>
      </Container>

      {/* ===== PROOF / TRUST ===== */}
      <Container maxWidth="lg" sx={{ pb: { xs: 6, md: 8 } }}>
        <Grid container spacing={3}>
          {[
            {
              icon: <BusinessIcon />,
              title: "Entities, projects & users in sync",
              desc:
                "Work ties together across entities, managers and team leads—so responsibilities are always clear.",
            },
            {
              icon: <ListAltIcon />,
              title: "Clean activity trail",
              desc:
                "Audit changes and actions with an activity log and login activity overview.",
            },
            {
              icon: <TimelineIcon />,
              title: "From overview to drill-down",
              desc:
                "Jump from dashboard totals to filtered lists and monthly roll-ups in a click.",
            },
          ].map((b) => (
            <Grid key={b.title} item xs={12} md={4}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  border: "1px solid #e7ecf5",
                  height: "100%",
                }}
              >
                <Stack spacing={1.25}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2,
                      bgcolor: "#f4f7ff",
                      color: "primary.main",
                      display: "grid",
                      placeItems: "center",
                    }}
                  >
                    {b.icon}
                  </Box>
                  <Typography variant="h6">{b.title}</Typography>
                  <Typography color="text.secondary">{b.desc}</Typography>
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* ===== FAQ ===== */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Typography variant="overline" color="text.secondary">
          FAQ
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 400, mb: 2 }}>
          Got more questions? We’ve got answers.
        </Typography>

        <Box sx={{ maxWidth: 1200 }}>
          {FAQS.map((item, idx) => (
            <Accordion
              key={idx}
              elevation={0}
              sx={{
                mb: 1.5,
                border: "1px solid #e7ecf5",
                borderRadius: 2,
                "&:before": { display: "none" },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2.5, py: 1.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 300 }}>
                  {item.q}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 2.5, pt: 0, pb: 2.5 }}>
                <Typography color="text.secondary">{item.a}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Container>

      {/* ===== FINAL CTA ===== */}
      <Box sx={{ py: { xs: 6, md: 8 }, background: "linear-gradient(180deg, #F8FAFF, #FFFFFF)" }}>
        <Container maxWidth="lg">
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 4 },
              borderRadius: 3,
              border: "1px solid #e7ecf5",
              textAlign: "center",
            }}
          >
            <Typography variant="h5" fontWeight={800}>
              Ready to simplify how your team works?
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
              Start with projects and tasks, add timesheets and insights when you’re ready.
            </Typography>
            <Stack spacing={2} direction={{ xs: "column", sm: "row" }} justifyContent="center">
              <Button component={RouterLink} to="/signup" size="large" variant="contained">
                Get Started
              </Button>
              <Button component={RouterLink} to="/projects" size="large" variant="outlined">
                Explore Projects
              </Button>
            </Stack>
          </Paper>
        </Container>
      </Box>

      <Divider />
    </Box>
  );
}
